/**
 * PR-38B-C — Push service-worker registration.
 * Guarded for Lovable preview / iframe / dev contexts. Only registers when:
 *   - production build
 *   - top-level window (not iframe)
 *   - service-worker API exists
 *   - hostname is NOT a Lovable preview/dev/sandbox host
 *
 * Exposes `showSwNotification` for retention dispatch — when a registration
 * is available, notifications survive the page being hidden/closed.
 */
const PREVIEW_HOST_PREFIXES = ["id-preview--", "preview--"];
const PREVIEW_HOST_SUFFIXES = [
  ".lovableproject.com",
  ".lovableproject-dev.com",
  ".beta.lovable.dev",
];
const PREVIEW_HOST_EXACT = ["lovableproject.com", "lovableproject-dev.com", "beta.lovable.dev"];

function isPreviewHost(host: string): boolean {
  if (PREVIEW_HOST_EXACT.includes(host)) return true;
  if (PREVIEW_HOST_PREFIXES.some((p) => host.startsWith(p))) return true;
  if (PREVIEW_HOST_SUFFIXES.some((s) => host.endsWith(s))) return true;
  return false;
}

function canRegister(): boolean {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!import.meta.env.PROD) return false;
  try { if (window.top !== window.self) return false; } catch { return false; }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("sw") === "off") return false;
    if (isPreviewHost(url.hostname)) return false;
  } catch { return false; }
  return true;
}

let registration: ServiceWorkerRegistration | null = null;
let registering: Promise<ServiceWorkerRegistration | null> | null = null;

export async function ensurePushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration;
  if (!canRegister()) return null;
  if (!registering) {
    registering = navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => { registration = reg; return reg; })
      .catch(() => null);
  }
  return registering;
}

/** Returns the active registration without triggering a fresh register. */
export function getPushServiceWorker(): ServiceWorkerRegistration | null {
  return registration;
}

export interface ShowSwNotificationInput {
  title: string;
  body: string;
  tag: string;
  deepLink?: string;
  silent?: boolean;
  kind?: string;
}

/**
 * Show a notification through the service worker registration when possible
 * (survives page being hidden/closed), falling back to `new Notification(...)`
 * if the worker is not registered yet.
 */
export async function showSwNotification(input: ShowSwNotificationInput): Promise<"sw" | "page" | "none"> {
  const reg = registration ?? (await ensurePushServiceWorker());
  if (reg && typeof reg.showNotification === "function") {
    try {
      await reg.showNotification(input.title, {
        body: input.body,
        tag: input.tag,
        icon: "/icon-512.png",
        badge: "/icon-512.png",
        silent: input.silent === true,
        data: { deepLink: input.deepLink ?? "/", kind: input.kind ?? null },
      });
      return "sw";
    } catch { /* fall through */ }
  }
  if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
    try {
      const n = new Notification(input.title, {
        body: input.body,
        tag: input.tag,
        icon: "/icon-512.png",
        badge: "/icon-512.png",
        silent: input.silent === true,
      });
      n.onclick = () => {
        try {
          window.focus();
          if (input.deepLink) window.location.assign(input.deepLink);
        } catch { /* noop */ }
      };
      return "page";
    } catch { /* noop */ }
  }
  return "none";
}